// 季節（ゲーム内サイクル）。v0.6.0 の追加システム。
//
// ゲーム内季節は 春→夏→秋→冬 を実時間 48 時間/季 で循環する。エポック基準の絶対時刻
// から決定的に算出する（同じ時刻なら必ず同じ季節＝再現可能・タイムゾーン非依存に近い）。
// 描画は季節パレットで地面・葉色・背景ティントを変える。客には季節ごとの来店係数を与える。
//
// 「季節の結晶」デコを設置している間は、季節サイクルを無視してその季節に固定する
// （撤去で自然サイクルへ戻る）。固定の判定は庭の中身から決まるのでこれも決定的。
//
// この層は描画・DOM・乱数に触れない純データ＋純関数。

/** 季節。 */
export type Season = 'spring' | 'summer' | 'autumn' | 'winter';

/** 季節の循環順（インデックス＝サイクル位置）。 */
export const SEASON_ORDER: readonly Season[] = ['spring', 'summer', 'autumn', 'winter'] as const;

/** 季節の日本語表示名。 */
export const SEASON_LABELS: Record<Season, string> = {
  spring: '春',
  summer: '夏',
  autumn: '秋',
  winter: '冬',
};

/** 季節のアイコン（ステータス画面・図鑑の見出しに使う絵文字）。 */
export const SEASON_ICONS: Record<Season, string> = {
  spring: '🌸',
  summer: '🌿',
  autumn: '🍁',
  winter: '❄️',
};

/** 1 季の実時間（ミリ秒）。48 時間/季。 */
export const SEASON_LENGTH_MS = 48 * 3600 * 1000;

/** 1 サイクル（4 季）の長さ（ミリ秒）。 */
export const CYCLE_LENGTH_MS = SEASON_LENGTH_MS * SEASON_ORDER.length;

/**
 * 季節算出のエポック基準（ミリ秒）。この時刻を「春の始まり」に合わせる。
 * 2026-01-01T00:00:00Z を基準にする（固定値なので全環境で同じ季節になる）。
 */
export const SEASON_EPOCH_MS = Date.UTC(2026, 0, 1, 0, 0, 0);

/**
 * 絶対時刻（エポックミリ秒）から自然な季節を算出する純関数。
 * エポックからの経過を 1 季ぶんで割った剰余で季節を決める。負時刻も正しく循環する。
 */
export function naturalSeasonForMs(ms: number): Season {
  const delta = ms - SEASON_EPOCH_MS;
  // 負値でも正しく [0, CYCLE) に収める。
  const inCycle = ((delta % CYCLE_LENGTH_MS) + CYCLE_LENGTH_MS) % CYCLE_LENGTH_MS;
  const idx = Math.floor(inCycle / SEASON_LENGTH_MS);
  return SEASON_ORDER[idx];
}

/**
 * 現在の季の「進捗」(0..1) を返す（季節ゲージ表示用）。0 = 季の始まり、1 に近い = 季の終わり。
 */
export function seasonProgressForMs(ms: number): number {
  const delta = ms - SEASON_EPOCH_MS;
  const inSeason = ((delta % SEASON_LENGTH_MS) + SEASON_LENGTH_MS) % SEASON_LENGTH_MS;
  return inSeason / SEASON_LENGTH_MS;
}

/**
 * 季節の結晶デコ ID → 固定する季節。これらのデコが庭にあるとサイクルを上書きして
 * その季節に固定する（複数あるときは SEASON_ORDER 順で最初の 1 つを採用＝決定的）。
 */
export const SEASON_CRYSTAL_BY_DECO: Record<string, Season> = {
  crystal_spring: 'spring',
  crystal_summer: 'summer',
  crystal_autumn: 'autumn',
  crystal_winter: 'winter',
};

/** デコ ID が季節の結晶か。 */
export function isSeasonCrystal(decoId: string): boolean {
  return decoId in SEASON_CRYSTAL_BY_DECO;
}

/**
 * 庭にある季節の結晶から「固定季節」を決める純関数（無ければ null）。
 * 複数種が置かれている場合は SEASON_ORDER 順で最初に見つかったものを採用する
 * （決定的・安定。実装は庭セルの decoId 集合を見るだけ）。
 *
 * @param decoIds 庭に置かれているデコ ID の集合（順不同で可）。
 */
export function fixedSeasonFrom(decoIds: Iterable<string>): Season | null {
  const present = new Set<string>();
  for (const id of decoIds) if (isSeasonCrystal(id)) present.add(id);
  if (present.size === 0) return null;
  for (const season of SEASON_ORDER) {
    for (const [deco, s] of Object.entries(SEASON_CRYSTAL_BY_DECO)) {
      if (s === season && present.has(deco)) return season;
    }
  }
  return null;
}

/**
 * 実効的な季節を返す。季節の結晶があればそれで固定、無ければ絶対時刻から自然算出。
 *
 * @param ms      絶対時刻（エポックミリ秒）。
 * @param decoIds 庭のデコ ID 集合（結晶固定の判定に使う）。
 */
export function effectiveSeason(ms: number, decoIds: Iterable<string>): Season {
  return fixedSeasonFrom(decoIds) ?? naturalSeasonForMs(ms);
}

/**
 * 客タイプ（好む季節）→ 季節ごとの来店係数。係数は小さめ（cozy 方針）。
 *  - 好む季節で 1.3、ニュートラルで 1.0、苦手季節で 0.8。
 *  - season=null（季節無関係の客）は常に 1.0。
 *
 * @param favorite 好む季節（null = 無関係）。
 * @param current  現在の季節。
 */
export function seasonMultiplier(favorite: Season | null, current: Season): number {
  if (favorite == null) return 1.0;
  if (favorite === current) return 1.3;
  // 対角（春↔秋, 夏↔冬）は苦手季節として少し下げる。
  const opposite: Record<Season, Season> = {
    spring: 'autumn',
    autumn: 'spring',
    summer: 'winter',
    winter: 'summer',
  };
  if (opposite[favorite] === current) return 0.8;
  return 1.0;
}

// =========================================================================
// 季節の別館（拡張島）（v0.8.1）
// =========================================================================
//
// 各拡張島に固定の季節を持たせ、「その季節の常連が一年じゅう通う別館」にする。
// 島を増やす＝対応できる季節（＝呼べる客の幅）が増える進捗エンジン。ホーム島（0）は
// 季節を持たず、移ろう自然季節（effectiveSeason）に従う。

/**
 * 拡張島の固定季節（島 index → 季節）。ホーム島(0)は null（自然季節に従う）。
 * 島1=春 / 島2=夏 / 島3=秋 / 島4=冬。
 */
export function islandSeason(islandIndex: number): Season | null {
  switch (islandIndex) {
    case 1: return 'spring';
    case 2: return 'summer';
    case 3: return 'autumn';
    case 4: return 'winter';
    default: return null;
  }
}

/** 季節の別館が「その季節の常連」に与える来店係数（ホームの移ろう季節 1.3 より強い＝定住）。 */
export const SEASON_FAVOR_ISLAND = 1.6;

/**
 * 客の来店に効く季節係数（v0.8.1）。ホームの移ろう季節（seasonMultiplier）と、
 * アンロック済みの季節別館の両方を考慮し、**好ましい方（最大）**を採用する。
 * 別館がその客の季節を持っていれば SEASON_FAVOR_ISLAND（定住の常連）まで底上げ。
 *
 * @param favorite      客の好む季節（null = 季節無関係）。
 * @param homeSeason    ホーム島の現在の季節（移ろう自然季節）。
 * @param islandSeasons アンロック済み拡張島の季節の集合。
 */
export function seasonalArrivalMultiplier(
  favorite: Season | null,
  homeSeason: Season,
  islandSeasons: Iterable<Season>,
): number {
  if (favorite == null) return 1.0;
  const home = seasonMultiplier(favorite, homeSeason);
  let island = 1.0;
  for (const s of islandSeasons) {
    if (s === favorite) {
      island = SEASON_FAVOR_ISLAND;
      break;
    }
  }
  return home > island ? home : island;
}
